import { useCallback, useEffect, useRef, useState } from "react";
import { searchBusinesses } from "@/services/publicApi";
import type { Business, BusinessSearchParams } from "@/types";

export interface BusinessSearchState {
  items: Business[];
  total: number;
  pages: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Runs a combined business search. Inputs are debounced (~220ms) so typing in
 * the keyword box does not fire a request on every keystroke. The search
 * service transparently falls back to demo data when no API is configured.
 */
export function useBusinessSearch(params: BusinessSearchParams): BusinessSearchState {
  const [items, setItems] = useState<Business[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const paramsRef = useRef(params);
  paramsRef.current = params;

  // A stable key so the effect re-runs only when a filter actually changes.
  const key = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      searchBusinesses(paramsRef.current)
        .then((result) => {
          if (cancelled) return;
          setItems(result.items);
          setTotal(result.total);
          setPages(result.pages);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : "Something went wrong while searching.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { items, total, pages, loading, error, reload };
}